import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Loader2, Plus, Package, CalendarIcon, ListOrdered, LayoutList, CalendarDays } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import POMultiFileUpload, { SelectedFileData, ExistingFileData } from './POMultiFileUpload';
import POLineItemsView from './POLineItemsView';
import POPdfViewerDialog from './POPdfViewerDialog';
import { Input } from '@/components/ui/input';
import MultiColumnFilter from '@/components/data-table/MultiColumnFilter';
import DataTablePagination from '@/components/data-table/DataTablePagination';
import { FilterOperation } from '@/hooks/useConnectionData';
import { applyColumnFilters } from '@/lib/applyColumnFilters';
import {
  PO_TABLE_COLUMNS,
  PO_DATE_FILTER_COLUMNS,
  POTableColumn,
  POListLayoutMode,
  buildPOTableRowData,
  canResellerEditPurchaseOrder,
  comparePurchaseOrders,
  getResellerPOEditBlockedMessage,
  partitionPurchaseOrdersByTodayPast,
} from './purchaseOrderTableUtils';
import PurchaseOrdersTable from './PurchaseOrdersTable';
import {
  getPoArchiveDisabledMessage,
  isPoArchiveDisabled,
} from './purchaseOrderArchiveUtils';

interface PurchaseOrderFile {
  id: string;
  purchase_order_id: string;
  file_name: string;
  file_type: string | null;
  file_size: number | null;
  storage_path: string;
  manufacturer_name: string | null;
  mpo_number: string | null;
  uploaded_by_email: string | null;
  uploaded_at: string | null;
}

interface PurchaseOrder {
  id: string;
  organisation_id: string;
  po_number: string;
  vendor_name: string;
  order_date: string;
  expected_delivery_date: string | null;
  status: string;
  total_amount: number | null;
  currency: string;
  notes: string | null;
  created_at: string;
  created_by_email: string | null;
  purchase_date: string | null;
  file_name: string | null;
  file_type: string | null;
  file_size: number | null;
  storage_path: string | null;
  manufacturer_name: string | null;
  uploaded_by_email: string | null;
  file_uploaded_at: string | null;
  files?: PurchaseOrderFile[];
  admin_viewed_at: string | null;
  archive_disabled: boolean;
  is_immutable: boolean;
  immutable_at: string | null;
  immutable_reason: string | null;
  archived_at: string | null;
  archived_by_email: string | null;
  archive_reason: string | null;
  _isSourceOrgPO?: boolean;
}

interface OrganisationPurchaseOrdersTabProps {
  organisation: {
    id: string;
    name: string;
    is_test_clone?: boolean;
    source_organisation_id?: string;
    data_cutoff_date?: string;
  };
  isAdmin?: boolean;
  isResellerPortal?: boolean;
}

const formatFileSize = (bytes: number | null): string => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const OrganisationPurchaseOrdersTab: React.FC<OrganisationPurchaseOrdersTabProps> = ({
  organisation,
  isAdmin = false,
  isResellerPortal = false,
}) => {
  const { toast } = useToast();
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPO, setEditingPO] = useState<PurchaseOrder | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<SelectedFileData[]>([]);

  // Edit mode state
  const [editManufacturerName, setEditManufacturerName] = useState('');
  const [editPurchaseDate, setEditPurchaseDate] = useState<Date | undefined>(undefined);
  const [manufacturers, setManufacturers] = useState<string[]>([]);
  const [loadingManufacturers, setLoadingManufacturers] = useState(false);
  const [editingPOFiles, setEditingPOFiles] = useState<PurchaseOrderFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);

  // Archive confirmation state
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [poToArchive, setPoToArchive] = useState<PurchaseOrder | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [activeTab, setActiveTab] = useState("orders");
  const [selectedPOIdForLineItems, setSelectedPOIdForLineItems] = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<
    Array<{ column: string; value: string; operation: FilterOperation }>
  >([]);
  const [sortColumn, setSortColumn] = useState<POTableColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const layoutStorageKey = `po-layout-mode-${organisation.id}`;
  const [layoutMode, setLayoutMode] = useState<POListLayoutMode>(() => {
    if (typeof window === 'undefined') return 'list';
    const stored = localStorage.getItem(layoutStorageKey);
    return stored === 'grouped' ? 'grouped' : 'list';
  });
  const [todayPage, setTodayPage] = useState(1);
  const [pastPage, setPastPage] = useState(1);
  const [todayRowsPerPage, setTodayRowsPerPage] = useState(25);
  const [pastRowsPerPage, setPastRowsPerPage] = useState(25);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [pdfDialogUrl, setPdfDialogUrl] = useState<string | null>(null);
  const [pdfDialogTitle, setPdfDialogTitle] = useState<string>('Purchase Order PDF');
  const isClone = !!organisation.is_test_clone && !!organisation.source_organisation_id;
  const cutoffDate = organisation.data_cutoff_date ?? null;
  const fetchPurchaseOrders = async () => {
    try {
      setLoading(true);

      let query = supabase
        .from('purchase_orders')
        .select('*')
        .eq('organisation_id', organisation.id);

      // Filter out archived POs for non-admin users
      if (!isAdmin) {
        query = query.neq('status', 'archived');
      }

      const { data: posData, error: posError } = await query.order('created_at', { ascending: false });

      if (posError) throw posError;

      // For clones, also fetch source org POs (up to cutoff date)
      let sourcePosData: any[] = [];
      if (isClone && organisation.source_organisation_id) {
        let sourceQuery = supabase
          .from('purchase_orders')
          .select('*')
          .eq('organisation_id', organisation.source_organisation_id);

        if (!isAdmin) {
          sourceQuery = sourceQuery.neq('status', 'archived');
        }
        if (cutoffDate) {
          sourceQuery = sourceQuery.lte('order_date', cutoffDate);
        }

        const { data: srcData, error: srcError } = await sourceQuery.order('created_at', { ascending: false });
        if (srcError) {
          console.error('Error fetching source org POs:', srcError);
        } else {
          sourcePosData = (srcData || []).map((po: any) => ({ ...po, _isSourceOrgPO: true }));
        }
      }

      const allPos = [...(posData || []), ...sourcePosData];
      const poIds = allPos.map(po => po.id);
      let filesData: PurchaseOrderFile[] = [];

      if (poIds.length > 0) {
        const { data: files, error: filesError } = await supabase
          .from('purchase_order_files')
          .select('*')
          .in('purchase_order_id', poIds)
          .order('uploaded_at', { ascending: false });

        if (filesError) throw filesError;
        filesData = files || [];
      }

      const posWithFiles = allPos.map(po => ({
        ...po,
        files: filesData.filter(f => f.purchase_order_id === po.id),
      }));

      setPurchaseOrders(posWithFiles as PurchaseOrder[]);
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
      toast({
        title: 'Error',
        description: 'Failed to load purchase orders',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchManufacturers = async () => {
    try {
      setLoadingManufacturers(true);
      const { data, error } = await supabase
        .from('manufacturers')
        .select('name')
        .eq('organisation_id', organisation.id)
        .order('name', { ascending: true });

      if (error) throw error;
      let names = (data || []).map((m) => m.name);

      // For clones, also fetch source org manufacturers
      if (isClone && organisation.source_organisation_id) {
        const { data: srcData } = await supabase
          .from('manufacturers')
          .select('name')
          .eq('organisation_id', organisation.source_organisation_id)
          .order('name', { ascending: true });
        if (srcData) {
          const srcNames = srcData.map((m) => m.name);
          names = [...new Set([...names, ...srcNames])].sort();
        }
      }

      setManufacturers(names);
    } catch (error) {
      console.error('Error fetching manufacturers:', error);
    } finally {
      setLoadingManufacturers(false);
    }
  };

  const fetchPOFiles = async (poId: string, fallbackMpoNumber?: string | null) => {
    try {
      setLoadingFiles(true);
      const { data, error } = await supabase
        .from('purchase_order_files')
        .select('*')
        .eq('purchase_order_id', poId)
        .order('uploaded_at', { ascending: false });

      if (error) throw error;
      setEditingPOFiles(
        (data || []).map((file) => ({
          ...file,
          mpo_number: file.mpo_number ?? fallbackMpoNumber ?? null,
        })),
      );
    } catch (error) {
      console.error('Error fetching PO files:', error);
      setEditingPOFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  };

  useEffect(() => {
    fetchPurchaseOrders();
  }, [organisation.id]);

  const resetForm = () => {
    setEditingPO(null);
    setSelectedFiles([]);
    setEditManufacturerName('');
    setEditPurchaseDate(undefined);
    setEditingPOFiles([]);
  };

  const handleOpenDialog = async (po?: PurchaseOrder) => {
    fetchManufacturers();

    if (po && isResellerPortal && !canResellerEditPurchaseOrder(po.status)) {
      toast({
        title: 'Cannot edit purchase order',
        description: getResellerPOEditBlockedMessage(po.status),
        variant: 'destructive',
      });
      return;
    }

    if (po) {
      setEditingPO(po);
      setEditManufacturerName(po.manufacturer_name || po.vendor_name || '');
      const dateStr = po.purchase_date || po.file_uploaded_at?.split('T')[0] || po.order_date;
      setEditPurchaseDate(dateStr ? new Date(dateStr) : undefined);

      // Mark as viewed when admin opens the record
      if (isAdmin && !po.admin_viewed_at) {
        await supabase
          .from('purchase_orders')
          .update({ admin_viewed_at: new Date().toISOString() })
          .eq('id', po.id)
          .is('admin_viewed_at', null);
      }

      fetchPOFiles(po.id, po.po_number);
    } else {
      setEditingPO(null);
      setEditManufacturerName('');
      setEditPurchaseDate(undefined);
      setEditingPOFiles([]);
    }
    setSelectedFiles([]);
    setDialogOpen(true);
  };

  const uploadFile = async (
    purchaseOrderId: string,
    fileData: SelectedFileData,
    manufacturerName: string,
    mpoNumber: string,
  ): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const formDataPayload = new FormData();
      formDataPayload.append('file', fileData.file);
      formDataPayload.append('purchaseOrderId', purchaseOrderId);
      formDataPayload.append('organisationId', organisation.id);
      formDataPayload.append('manufacturerName', manufacturerName);
      formDataPayload.append('mpoNumber', mpoNumber);
      if (fileData.fileHash) {
        formDataPayload.append('fileHash', fileData.fileHash);
      }
      formDataPayload.append('uploaderEmail', user?.email || '');

      const { data, error } = await invokeFunction(supabase, 'upload-po-file-v3', {
        body: formDataPayload,
      });

      if (error) {
        console.error('Upload error:', error);
        throw new Error(error.message || 'Failed to upload file');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to upload file');
      }

      return true;
    } catch (error) {
      console.error('File upload error:', error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    // Edit mode validation
    if (editingPO) {
      if (isResellerPortal && !canResellerEditPurchaseOrder(editingPO.status)) {
        toast({
          title: 'Cannot edit purchase order',
          description: getResellerPOEditBlockedMessage(editingPO.status),
          variant: 'destructive',
        });
        return;
      }

      if (!editManufacturerName.trim()) {
        toast({
          title: 'Validation Error',
          description: 'Please select a manufacturer',
          variant: 'destructive',
        });
        return;
      }

      const filesWithoutMpo = editingPOFiles.filter(f => !f.mpo_number?.trim());
      if (filesWithoutMpo.length > 0) {
        toast({
          title: 'Validation Error',
          description: `Please enter an mPO number for each file (${filesWithoutMpo.length} file(s) missing)`,
          variant: 'destructive',
        });
        return;
      }
    } else {
      // Create mode: require at least one file
      if (selectedFiles.length === 0) {
        toast({
          title: 'Validation Error',
          description: 'Please select at least one file to upload',
          variant: 'destructive',
        });
        return;
      }

      // Check each file has a manufacturer
      const filesWithoutManufacturer = selectedFiles.filter(f => !f.manufacturerName.trim());
      if (filesWithoutManufacturer.length > 0) {
        toast({
          title: 'Validation Error',
          description: `Please select a manufacturer for each file (${filesWithoutManufacturer.length} file(s) missing)`,
          variant: 'destructive',
        });
        return;
      }

      const filesWithoutMpo = selectedFiles.filter(f => !f.mpoNumber.trim());
      if (filesWithoutMpo.length > 0) {
        toast({
          title: 'Validation Error',
          description: `Please enter an mPO number for each file (${filesWithoutMpo.length} file(s) missing)`,
          variant: 'destructive',
        });
        return;
      }
    }

    if (selectedFiles.some(f => f.fileHash === undefined)) {
      toast({
        title: 'Please wait',
        description: 'Files are still being processed. Try again in a moment.',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (editingPO) {
        // Edit mode: update single PO
        const updateData: Record<string, unknown> = {
          manufacturer_name: editManufacturerName || editingPO.manufacturer_name,
          vendor_name: editManufacturerName || editingPO.vendor_name,
          purchase_date: editPurchaseDate ? format(editPurchaseDate, 'yyyy-MM-dd') : editingPO.purchase_date,
        };

        const { error } = await supabase
          .from('purchase_orders')
          .update(updateData)
          .eq('id', editingPO.id);

        if (error) throw error;

        // Update manufacturer and mPO number for each file
        for (const file of editingPOFiles) {
          await supabase
            .from('purchase_order_files')
            .update({
              manufacturer_name: editManufacturerName,
              mpo_number: file.mpo_number?.trim() || null,
            })
            .eq('id', file.id);
        }

        // Keep purchase_orders.po_number aligned when a single file remains
        if (editingPOFiles.length === 1 && editingPOFiles[0].mpo_number?.trim()) {
          await supabase
            .from('purchase_orders')
            .update({ po_number: editingPOFiles[0].mpo_number.trim() })
            .eq('id', editingPO.id);
        }

        // Upload new files for this PO
        if (selectedFiles.length > 0) {
          setUploadingFile(true);
          for (const fileData of selectedFiles) {
            await uploadFile(
              editingPO.id,
              fileData,
              editManufacturerName,
              fileData.mpoNumber.trim(),
            );
          }
          setUploadingFile(false);
        }

        toast({ title: 'Success', description: 'Purchase order updated' });
      } else {
        // Create mode: create one PO per file
        setUploadingFile(true);
        let successCount = 0;
        let failCount = 0;

        for (const fileData of selectedFiles) {
          try {
            // Use the per-file purchase date
            const purchaseDate = fileData.purchaseDate
              ? format(fileData.purchaseDate, 'yyyy-MM-dd')
              : new Date().toISOString().split('T')[0];

            // Create a new PO for this file with 'uploaded' status
            const poData = {
              organisation_id: organisation.id,
              po_number: fileData.mpoNumber.trim(),
              vendor_name: fileData.manufacturerName,
              order_date: purchaseDate,
              status: 'uploaded',
              created_by_email: user?.email || null,
              purchase_date: purchaseDate,
              manufacturer_name: fileData.manufacturerName,
            };

            const { data: newPO, error: poError } = await supabase
              .from('purchase_orders')
              .insert(poData)
              .select('id')
              .single();

            if (poError) throw poError;

            // Upload file for this PO
            await uploadFile(newPO.id, fileData, fileData.manufacturerName, fileData.mpoNumber.trim());
            successCount++;
          } catch (uploadError) {
            console.error('PO creation failed:', uploadError);
            failCount++;
          }
        }

        setUploadingFile(false);

        if (failCount > 0) {
          toast({
            title: 'Partial Success',
            description: `${successCount} PO(s) created successfully, ${failCount} failed`,
            variant: 'destructive',
          });
        } else {
          toast({
            title: 'Success',
            description: `${successCount} Purchase Order${successCount > 1 ? 's' : ''} created`,
          });
        }
      }

      setDialogOpen(false);
      resetForm();
      fetchPurchaseOrders();
    } catch (error) {
      console.error('Error saving purchase order:', error);
      toast({
        title: 'Error',
        description: 'Failed to save purchase order',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleArchiveClick = (po: PurchaseOrder) => {
    setPoToArchive(po);
    setArchiveReason('');
    setArchiveDialogOpen(true);
  };

  const handleConfirmArchive = async () => {
    if (!poToArchive || !archiveReason.trim()) return;

    if (isPoArchiveDisabled(poToArchive.archive_disabled)) {
      toast({
        title: 'Cannot archive',
        description: getPoArchiveDisabledMessage(),
        variant: 'destructive',
      });
      return;
    }

    setArchiving(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const userEmail = userData.user?.email || 'unknown';

      // Use the archive_purchase_order RPC function
      const { error } = await (supabase.rpc as any)('archive_purchase_order', {
        p_purchase_order_id: poToArchive.id,
        p_actor_email: userEmail,
        p_reason: archiveReason.trim(),
      });

      if (error) throw error;

      toast({ title: 'Success', description: 'Purchase order archived' });
      fetchPurchaseOrders();
    } catch (error) {
      console.error('Error archiving purchase order:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to archive purchase order',
        variant: 'destructive',
      });
    } finally {
      setArchiving(false);
      setArchiveDialogOpen(false);
      setPoToArchive(null);
      setArchiveReason('');
    }
  };

  const handleUnarchive = async (po: PurchaseOrder) => {
    try {
      const { error } = await supabase
        .from('purchase_orders')
        .update({
          status: 'uploaded',
          archived_at: null,
          archived_by_email: null,
          archive_reason: null,
        })
        .eq('id', po.id);

      if (error) throw error;

      toast({ title: 'Success', description: 'Purchase order unarchived' });
      fetchPurchaseOrders();
    } catch (error) {
      console.error('Error unarchiving purchase order:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to unarchive purchase order',
        variant: 'destructive',
      });
    }
  };

  const handleViewFile = async (poId: string, storagePath: string, fileName: string) => {
    try {
      // Mark as viewed by admin when admin views file
      if (isAdmin) {
        await supabase
          .from('purchase_orders')
          .update({ admin_viewed_at: new Date().toISOString() })
          .eq('id', poId)
          .is('admin_viewed_at', null);
      }

      const viewerUrl = `/api/serve-po-file?path=${encodeURIComponent(storagePath)}&poId=${encodeURIComponent(poId)}`;
      setPdfDialogUrl(viewerUrl);
      setPdfDialogTitle(fileName);
      setPdfDialogOpen(true);
    } catch (error) {
      console.error('Error viewing file:', error);
      toast({
        title: 'Error',
        description: 'Failed to view file',
        variant: 'destructive',
      });
    }
  };

  const getExistingFiles = (): ExistingFileData[] => {
    return editingPOFiles.map(f => ({
        id: f.id,
        file_name: f.file_name,
        file_type: f.file_type,
        file_size: f.file_size,
        storage_path: f.storage_path,
        mpo_number: f.mpo_number ?? editingPO?.po_number ?? null,
      }));
  };

  const handleExistingFileMpoNumberChange = (fileId: string, mpoNumber: string) => {
    setEditingPOFiles(prev =>
      prev.map(f => (f.id === fileId ? { ...f, mpo_number: mpoNumber } : f)),
    );
  };

  const handleManufacturerAdded = (name: string) => {
    setManufacturers(prev => [...prev, name].sort());
  };

  // Check if all files have manufacturers and mPO numbers (for create mode button state)
  const allFilesHaveManufacturers = selectedFiles.every(f => f.manufacturerName.trim());
  const allFilesHaveMpoNumbers = selectedFiles.every(f => f.mpoNumber.trim());
  const allExistingFilesHaveMpoNumbers = editingPO
    ? editingPOFiles.every(f => f.mpo_number?.trim())
    : true;

  const poTableRowData = useMemo(
    () => purchaseOrders.map((po) => ({ po, ...buildPOTableRowData(po) })),
    [purchaseOrders],
  );

  const addFilter = useCallback(
    (column: string, value: string, operation: FilterOperation) => {
      setCurrentPage(1);
      setActiveFilters((prev) => {
        const existingIndex = prev.findIndex(
          (f) => f.column === column && f.value === value && f.operation === operation,
        );
        if (existingIndex >= 0) {
          return prev.filter((_, i) => i !== existingIndex);
        }
        return [...prev, { column, value, operation }];
      });
    },
    [],
  );

  const clearFilters = useCallback(() => {
    setCurrentPage(1);
    setActiveFilters([]);
  }, []);

  const handleSort = useCallback((column: POTableColumn) => {
    setCurrentPage(1);
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }, [sortColumn]);

  const displayPurchaseOrders = useMemo(() => {
    const filteredRows = applyColumnFilters(poTableRowData, activeFilters);
    const sorted = [...filteredRows];
    if (sortColumn) {
      sorted.sort((a, b) => comparePurchaseOrders(a.po, b.po, sortColumn, sortDirection));
    }
    return sorted.map((row) => row.po);
  }, [poTableRowData, activeFilters, sortColumn, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(displayPurchaseOrders.length / rowsPerPage));

  const paginatedPurchaseOrders = useMemo(() => {
    const safePage = Math.min(currentPage, totalPages);
    const start = (safePage - 1) * rowsPerPage;
    return displayPurchaseOrders.slice(start, start + rowsPerPage);
  }, [displayPurchaseOrders, currentPage, rowsPerPage, totalPages]);

  const { today: todayPurchaseOrders, past: pastPurchaseOrders } = useMemo(
    () => partitionPurchaseOrdersByTodayPast(displayPurchaseOrders),
    [displayPurchaseOrders],
  );

  const todayTotalPages = Math.max(1, Math.ceil(todayPurchaseOrders.length / todayRowsPerPage));
  const pastTotalPages = Math.max(1, Math.ceil(pastPurchaseOrders.length / pastRowsPerPage));

  const paginatedTodayPurchaseOrders = useMemo(() => {
    const safePage = Math.min(todayPage, todayTotalPages);
    const start = (safePage - 1) * todayRowsPerPage;
    return todayPurchaseOrders.slice(start, start + todayRowsPerPage);
  }, [todayPurchaseOrders, todayPage, todayRowsPerPage, todayTotalPages]);

  const paginatedPastPurchaseOrders = useMemo(() => {
    const safePage = Math.min(pastPage, pastTotalPages);
    const start = (safePage - 1) * pastRowsPerPage;
    return pastPurchaseOrders.slice(start, start + pastRowsPerPage);
  }, [pastPurchaseOrders, pastPage, pastRowsPerPage, pastTotalPages]);

  useEffect(() => {
    localStorage.setItem(layoutStorageKey, layoutMode);
  }, [layoutMode, layoutStorageKey]);

  useEffect(() => {
    setCurrentPage(1);
    setTodayPage(1);
    setPastPage(1);
  }, [activeFilters, sortColumn, sortDirection, layoutMode]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (todayPage > todayTotalPages) {
      setTodayPage(todayTotalPages);
    }
  }, [todayPage, todayTotalPages]);

  useEffect(() => {
    if (pastPage > pastTotalPages) {
      setPastPage(pastTotalPages);
    }
  }, [pastPage, pastTotalPages]);

  const handleLayoutModeChange = (value: string) => {
    if (value === 'list' || value === 'grouped') {
      setLayoutMode(value);
    }
  };

  const renderSortIndicator = (column: POTableColumn) => {
    if (sortColumn !== column) {
      return <span className="text-xs text-muted-foreground">↕</span>;
    }
    return (
      <span className="text-xs text-muted-foreground">
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const purchaseOrdersTableProps = {
    isAdmin,
    isResellerPortal,
    sortColumn,
    sortDirection,
    onSort: handleSort,
    renderSortIndicator,
    onViewFile: handleViewFile,
    onOpenDialog: handleOpenDialog,
    onArchiveClick: handleArchiveClick,
    onUnarchive: handleUnarchive,
    onViewLineItems: (poId: string) => {
      setSelectedPOIdForLineItems(poId);
      setActiveTab('line-items');
    },
    onStatusChange: fetchPurchaseOrders,
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12" role="status">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-4">
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Purchase Orders
              </CardTitle>
              <TabsList>
                <TabsTrigger value="orders" className="flex items-center gap-1">
                  <Package className="h-4 w-4" />
                  Orders
                </TabsTrigger>
                {!isResellerPortal && (
                  <TabsTrigger value="line-items" className="flex items-center gap-1">
                    <ListOrdered className="h-4 w-4" />
                    Line Items
                  </TabsTrigger>
                )}
              </TabsList>
            </div>
            <div className="flex items-center gap-2">
              {purchaseOrders.length > 0 && activeTab === 'orders' && (
                <ToggleGroup
                  type="single"
                  value={layoutMode}
                  onValueChange={handleLayoutModeChange}
                  aria-label="Purchase orders layout"
                >
                  <ToggleGroupItem value="list" aria-label="List layout" data-testid="po-layout-list">
                    <LayoutList className="h-4 w-4" />
                  </ToggleGroupItem>
                  <ToggleGroupItem value="grouped" aria-label="Today and past layout" data-testid="po-layout-grouped">
                    <CalendarDays className="h-4 w-4" />
                  </ToggleGroupItem>
                </ToggleGroup>
              )}
              {purchaseOrders.length > 0 && activeTab === 'orders' && (
                <MultiColumnFilter
                  columns={[...PO_TABLE_COLUMNS]}
                  dateColumns={[...PO_DATE_FILTER_COLUMNS]}
                  activeFilters={activeFilters}
                  onAddFilter={addFilter}
                  onClearAllFilters={clearFilters}
                  tableData={poTableRowData}
                />
              )}
              <Button onClick={() => handleOpenDialog()} data-testid="create-po-button">
                <Plus className="h-4 w-4 mr-2" />
                Upload Purchase Order
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <TabsContent value="orders" className="mt-0">
              {purchaseOrders.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No purchase orders found</p>
                  <p className="text-sm mt-1">Create your first purchase order to get started</p>
                </div>
              ) : displayPurchaseOrders.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No purchase orders match your filters</p>
                  <p className="text-sm mt-1">Adjust or clear filters to see more results</p>
                </div>
              ) : layoutMode === 'list' ? (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span>Page size:</span>
                      <Select
                        value={rowsPerPage.toString()}
                        onValueChange={(value) => {
                          setRowsPerPage(Number(value));
                          setCurrentPage(1);
                        }}
                      >
                        <SelectTrigger className="w-20" data-testid="po-page-size">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="25">25</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {displayPurchaseOrders.length} purchase order
                      {displayPurchaseOrders.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <PurchaseOrdersTable
                    purchaseOrders={paginatedPurchaseOrders}
                    {...purchaseOrdersTableProps}
                  />
                  {totalPages > 1 && (
                    <div className="mt-4">
                      <div className="text-sm text-center text-muted-foreground mb-2">
                        Showing page {Math.min(currentPage, totalPages)} of {totalPages} (
                        {paginatedPurchaseOrders.length} of {displayPurchaseOrders.length} entries)
                      </div>
                      <DataTablePagination
                        currentPage={Math.min(currentPage, totalPages)}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-8">
                  <section className="space-y-4" data-testid="po-section-today">
                    <h3 className="text-lg font-semibold">
                      Today ({todayPurchaseOrders.length})
                    </h3>
                    {todayPurchaseOrders.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4">
                        No purchase orders uploaded today
                      </p>
                    ) : (
                      <>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span>Page size:</span>
                            <Select
                              value={todayRowsPerPage.toString()}
                              onValueChange={(value) => {
                                setTodayRowsPerPage(Number(value));
                                setTodayPage(1);
                              }}
                            >
                              <SelectTrigger className="w-20" data-testid="po-today-page-size">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="25">25</SelectItem>
                                <SelectItem value="50">50</SelectItem>
                                <SelectItem value="100">100</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {todayPurchaseOrders.length} purchase order
                            {todayPurchaseOrders.length === 1 ? '' : 's'}
                          </span>
                        </div>
                        <PurchaseOrdersTable
                          purchaseOrders={paginatedTodayPurchaseOrders}
                          {...purchaseOrdersTableProps}
                        />
                        {todayTotalPages > 1 && (
                          <div className="mt-4">
                            <div className="text-sm text-center text-muted-foreground mb-2">
                              Showing page {Math.min(todayPage, todayTotalPages)} of {todayTotalPages} (
                              {paginatedTodayPurchaseOrders.length} of {todayPurchaseOrders.length} entries)
                            </div>
                            <DataTablePagination
                              currentPage={Math.min(todayPage, todayTotalPages)}
                              totalPages={todayTotalPages}
                              onPageChange={setTodayPage}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </section>
                  <section className="space-y-4" data-testid="po-section-past">
                    <h3 className="text-lg font-semibold">
                      Past ({pastPurchaseOrders.length})
                    </h3>
                    {pastPurchaseOrders.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4">
                        No past purchase orders
                      </p>
                    ) : (
                      <>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span>Page size:</span>
                            <Select
                              value={pastRowsPerPage.toString()}
                              onValueChange={(value) => {
                                setPastRowsPerPage(Number(value));
                                setPastPage(1);
                              }}
                            >
                              <SelectTrigger className="w-20" data-testid="po-past-page-size">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="25">25</SelectItem>
                                <SelectItem value="50">50</SelectItem>
                                <SelectItem value="100">100</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {pastPurchaseOrders.length} purchase order
                            {pastPurchaseOrders.length === 1 ? '' : 's'}
                          </span>
                        </div>
                        <PurchaseOrdersTable
                          purchaseOrders={paginatedPastPurchaseOrders}
                          {...purchaseOrdersTableProps}
                        />
                        {pastTotalPages > 1 && (
                          <div className="mt-4">
                            <div className="text-sm text-center text-muted-foreground mb-2">
                              Showing page {Math.min(pastPage, pastTotalPages)} of {pastTotalPages} (
                              {paginatedPastPurchaseOrders.length} of {pastPurchaseOrders.length} entries)
                            </div>
                            <DataTablePagination
                              currentPage={Math.min(pastPage, pastTotalPages)}
                              totalPages={pastTotalPages}
                              onPageChange={setPastPage}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </section>
                </div>
              )}
            </TabsContent>
            {!isResellerPortal && (
              <TabsContent value="line-items" className="mt-0">
                <POLineItemsView
                  purchaseOrders={purchaseOrders.filter(po => po.status !== 'archived').map(po => ({
                    id: po.id,
                    po_number: po.po_number,
                    manufacturer_name: po.manufacturer_name,
                    vendor_name: po.vendor_name,
                    status: po.status,
                    order_date: po.order_date,
                    total_amount: po.total_amount,
                    files: po.files?.map(f => ({ id: f.id, file_name: f.file_name, file_type: f.file_type, storage_path: f.storage_path })),
                  }))}
                  organisationId={organisation.id}
                  isAdmin={isAdmin}
                  isResellerPortal={isResellerPortal}
                  initialSelectedPOId={selectedPOIdForLineItems}
                />
              </TabsContent>
            )}
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingPO ? 'Edit Purchase Order' : 'Upload Purchase Orders'}
              </DialogTitle>
              <DialogDescription>
                {editingPO
                  ? 'Update the purchase order details.'
                  : 'Upload files to create purchase orders. Each file will create a separate PO.'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              {editingPO && (
                <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
                  <h3 className="font-medium text-sm">Purchase Order Details</h3>

                  <div className="space-y-2">
                    <Label htmlFor="edit-manufacturer">Manufacturer *</Label>
                    <Select
                      value={editManufacturerName}
                      onValueChange={setEditManufacturerName}
                      disabled={submitting || uploadingFile || loadingManufacturers}
                    >
                      <SelectTrigger id="edit-manufacturer">
                        <SelectValue placeholder="Select manufacturer" />
                      </SelectTrigger>
                      <SelectContent className="bg-popover">
                        {editManufacturerName && !manufacturers.includes(editManufacturerName) && (
                          <SelectItem value={editManufacturerName}>
                            {editManufacturerName}
                          </SelectItem>
                        )}
                        {manufacturers.map((name) => (
                          <SelectItem key={name} value={name}>
                            {name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Purchase Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-full justify-start text-left font-normal',
                            !editPurchaseDate && 'text-muted-foreground',
                          )}
                          disabled={submitting || uploadingFile}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {editPurchaseDate ? format(editPurchaseDate, 'PPP') : <span>Pick a date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={editPurchaseDate}
                          onSelect={setEditPurchaseDate}
                          initialFocus
                          className={cn('p-3 pointer-events-auto')}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <h3 className="font-medium text-sm">{editingPO ? 'Files' : 'Upload Files'}</h3>
                <POMultiFileUpload
                  onFilesChanged={setSelectedFiles}
                  existingFiles={editingPO ? getExistingFiles() : []}
                  onExistingFileMpoNumberChange={editingPO ? handleExistingFileMpoNumberChange : undefined}
                  disableNewUploads={!!editingPO}
                  disabled={submitting || uploadingFile}
                  manufacturers={manufacturers}
                  organisationId={organisation.id}
                  onManufacturerAdded={handleManufacturerAdded}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting || uploadingFile}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={
                  submitting ||
                  uploadingFile ||
                  (!editingPO &&
                    (selectedFiles.length === 0 ||
                      !allFilesHaveManufacturers ||
                      !allFilesHaveMpoNumbers)) ||
                  (!!editingPO && !allExistingFilesHaveMpoNumbers)
                }
              >
                {(submitting || uploadingFile) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {uploadingFile
                  ? 'Uploading...'
                  : editingPO
                    ? 'Update'
                    : selectedFiles.length > 0
                      ? `Create ${selectedFiles.length} PO${selectedFiles.length > 1 ? 's' : ''}`
                      : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Archive Purchase Order</AlertDialogTitle>
              <AlertDialogDescription>
                This will archive the purchase order. It will be hidden from the main list but preserved for audit purposes.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-2 py-4">
              <Label htmlFor="archive-reason">Reason for archiving *</Label>
              <Input
                id="archive-reason"
                placeholder="e.g., Duplicate order, Cancelled by vendor..."
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                disabled={archiving}
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={archiving}>Cancel</AlertDialogCancel>
              <Button
                onClick={handleConfirmArchive}
                disabled={archiving || !archiveReason.trim()}
                variant="destructive"
              >
                {archiving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Archive
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Tabs>

      <POPdfViewerDialog
        open={pdfDialogOpen}
        onOpenChange={setPdfDialogOpen}
        url={pdfDialogUrl}
        title={pdfDialogTitle}
      />
    </>
  );
};

export default OrganisationPurchaseOrdersTab;
